import { useCallback, useRef, useState } from 'react'

import useOnClickOutside from 'commons/customHooks/useOnClickOutside'
import i18n from 'commons/libs/i18n'
import { HavLogo, Icon, type IconName, Modal, Typography } from 'components'
import { useLocation } from 'react-router-dom'
import { setOpenRecommendationModal } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import type { ScreenPerm } from '../Header/constants'
import { handleRecommendationReportDownload } from './helpers'
import { Menu } from './Menu'
import { MenuItemNode } from './MenuItemNode'
import * as S from './styles'

export type MenuItem = {
  itemId: string
  titleKey: string
  leftIcon?: IconName
  to?: string | null
  permission?: ScreenPerm | ScreenPerm[]
  menuItems?: MenuItem[]
}

export type MenuSection = {
  titleKey?: string
  menuLists: MenuItem[]
}

interface MenuSidebarProps {
  menu: MenuSection[]
  footer?: MenuItem[]
  showLogo?: boolean
}

export const useMenuActive = () => {
  const { pathname } = useLocation()

  const isItemActive = (item: MenuItem, links: string[]) => {
    const selected = item.to === pathname

    const active =
      item.menuItems?.some((item) => item.to === pathname || links.includes(pathname)) ?? false

    return { active, selected }
  }

  return { isItemActive }
}

export const MenuSidebar = ({ menu, footer, showLogo }: MenuSidebarProps) => {
  const dispatch = useDispatch()
  const [collapsed, setCollapsed] = useState(true)
  const [openItems, setOpenItems] = useState<string[]>([])
  const { openRecommendationModal } = useSelector((state) => state.structure)

  const wrapperRef = useRef<HTMLDivElement>(null)

  useOnClickOutside(wrapperRef, () => {
    setCollapsed(true)
    setOpenItems([])
  })

  const handleExpandToggle = () => {
    setCollapsed((prev) => !prev)
    setOpenItems([])
  }

  const handleSubmenuToggle = useCallback(
    (itemId: string, level: number) => {
      setOpenItems((prev) => {
        const isOpen = prev.includes(itemId)
        const newOpenItems = isOpen ? prev.filter((i) => i !== itemId) : [...prev, itemId]

        // If collapsed, allow only one level 0 item to be open
        if (collapsed && level === 0 && !isOpen) {
          return [itemId]
        }

        return newOpenItems
      })
    },
    [collapsed],
  )

  const setRecommendationModal = (open: boolean) => {
    dispatch(setOpenRecommendationModal(open))
  }

  const handleRecommendationDownload = async () => {
    setRecommendationModal(false)
    handleRecommendationReportDownload()
  }

  return (
    <S.Wrapper collapsed={collapsed} submenuOpen={openItems.length > 0}>
      <S.MenuSideBarWrapper collapsed={collapsed} data-testid="menu-sidebar" ref={wrapperRef}>
        <S.ExpandButtonWrapper collapsed={collapsed}>
          <S.LogoWrapper collapsed={collapsed}>
            {showLogo && <HavLogo color={theme.colors.secondary} />}
          </S.LogoWrapper>
          <S.ExpandButton
            collapsed={collapsed}
            data-testid="expand-button"
            onClick={handleExpandToggle}
          >
            <Icon icon="arrow-forward" size="22" />
          </S.ExpandButton>
        </S.ExpandButtonWrapper>

        <Menu
          collapsed={collapsed}
          menu={menu}
          onSubmenuToggle={handleSubmenuToggle}
          openItems={openItems}
        />

        {footer && footer.length > 0 && (
          <S.Footer>
            {footer.map((item, index) => (
              <MenuItemNode
                collapsed={collapsed}
                item={item}
                key={`${item.itemId}-${index}`}
                level={0}
                onSubmenuToggle={handleSubmenuToggle}
                openItems={openItems}
              />
            ))}
          </S.Footer>
        )}
      </S.MenuSideBarWrapper>

      <Modal
        buttonAction={handleRecommendationDownload}
        buttonIcon={<Icon icon="download" size="16" />}
        buttonText={i18n.t('buttons.download')}
        closeAction={() => setRecommendationModal(false)}
        description={
          <Typography design="hav-default-03-text">
            <strong>{i18n.t('components.recommendationModal.downloadSpreadsheet')}</strong>{' '}
            {i18n.t('components.recommendationModal.getCustomList')}
          </Typography>
        }
        open={openRecommendationModal}
        returnText={i18n.t('buttons.back')}
        setOpen={setRecommendationModal}
        showReturn
        title={i18n.t('components.recommendationModal.title')}
        titleIcon="table"
        titleSize={20}
      />
    </S.Wrapper>
  )
}
